// Admin Service for managing organization settings and M2M clients via admin API
// Communicates with the Lambda admin API endpoints

class AdminService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    this.authToken = null;
  }

  // Set the JWT token directly (called from React components)
  setAuthToken(token) {
    this.authToken = token;
  }

  // Get JWT token for API calls
  getAuthToken() {
    return this.authToken;
  }

  // Common headers for authenticated requests
  getHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Handle API response and errors
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      const error = new Error(errorData.error || `HTTP ${response.status}`);
      // Attach machine-readable fields so callers can branch on specific errors
      // (e.g., 409 version_mismatch on EULA accept -> re-fetch and re-display)
      error.status = response.status;
      error.errorCode = errorData.error_code;
      error.data = errorData;
      throw error;
    }
    return response.json();
  }

  // === ORGANIZATION MANAGEMENT ===

  // Get organization details including profile and address information
  async getOrganization() {
    console.log('🔍 Fetching organization details...');
    
    try {
      const response = await fetch(`${this.baseURL}/admin/organizations`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log('✅ Organization details loaded successfully');
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch organization details:', error);
      throw error;
    }
  }

  // Update organization profile and/or address information
  async updateOrganization(updateData) {
    console.log('🔄 Updating organization data:', updateData);
    
    try {
      const response = await fetch(`${this.baseURL}/admin/organizations`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updateData)
      });

      const data = await this.handleResponse(response);
      console.log('✅ Organization updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Failed to update organization:', error);
      throw error;
    }
  }

  // Update profile information only
  async updateProfile(profileData) {
    return this.updateOrganization({ profile: profileData });
  }

  // Update organization information only  
  async updateOrganizationDetails(orgData) {
    return this.updateOrganization({ organization: orgData });
  }

  // === M2M CLIENT MANAGEMENT ===

  // List all M2M clients for the current organization
  async listClients() {
    console.log('🔍 Fetching M2M clients...');
    
    try {
      const response = await fetch(`${this.baseURL}/admin/m2m/clients`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log(`✅ Found ${data.total} M2M clients`);
      return data;
    } catch (error) {
      console.error('❌ Failed to list M2M clients:', error);
      throw error;
    }
  }

  // Create a new M2M client
  async createClient(clientData) {
    console.log('🔨 Creating M2M client:', clientData.client_name);
    
    try {
      const response = await fetch(`${this.baseURL}/admin/m2m/clients`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(clientData)
      });

      const data = await this.handleResponse(response);
      console.log('✅ M2M client created:', data.client_id);
      return data;
    } catch (error) {
      console.error('❌ Failed to create M2M client:', error);
      throw error;
    }
  }

  // Delete an M2M client
  async deleteClient(clientId) {
    console.log('🗑️ Deleting M2M client:', clientId);
    
    try {
      const response = await fetch(`${this.baseURL}/admin/m2m/clients/${clientId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log('✅ M2M client deleted');
      return data;
    } catch (error) {
      console.error('❌ Failed to delete M2M client:', error);
      throw error;
    }
  }

  // Rotate the secret for an M2M client
  async rotateSecret(clientId) {
    console.log('🔄 Rotating secret for M2M client:', clientId);
    
    try {
      const response = await fetch(`${this.baseURL}/admin/m2m/clients/${clientId}/rotate-secret`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log('✅ M2M client secret rotated');
      return data;
    } catch (error) {
      console.error('❌ Failed to rotate M2M client secret:', error);
      throw error;
    }
  }

  // Test an M2M token by making a request to the proxy
  async testToken(token) {
    console.log('🧪 Testing M2M token...');
    
    try {
      const response = await fetch(`${this.baseURL}/proxy/v1/traces`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Lumoz-API-Key': token
        },
        body: JSON.stringify({
          test: 'token_validation',
          timestamp: new Date().toISOString()
        })
      });

      // For token testing, even 502 (OTEL collector not available) means auth worked
      if (response.status === 200 || response.status === 502) {
        console.log('✅ M2M token is valid');
        return { valid: true, status: response.status };
      } else if (response.status === 403) {
        console.log('❌ M2M token is invalid');
        return { valid: false, status: response.status };
      } else {
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Token test failed:', error);
      throw error;
    }
  }

  // Format client data for display
  formatClientForDisplay(client) {
    return {
      ...client,
      displayName: client.client_name || client.client_id,
      maskedSecret: client.client_secret ? 
        `***${client.client_secret.slice(-6)}` : 
        'Hidden',
      createdDate: client.created_at ? 
        new Date(client.created_at).toLocaleDateString() : 
        'Unknown',
      lastUsedDate: client.last_used_at ? 
        new Date(client.last_used_at).toLocaleDateString() : 
        'Never'
    };
  }

  // Estimate cost based on usage (placeholder logic)
  estimateCost(client) {
    // This would typically come from actual usage metrics
    // For now, return a placeholder based on last usage
    if (!client.last_used_at) {
      return '$0.00';
    }

    const lastUsed = new Date(client.last_used_at);
    const daysSinceUsed = Math.floor((Date.now() - lastUsed.getTime()) / (1000 * 60 * 60 * 24));

    // Fake cost calculation for demo
    if (daysSinceUsed < 1) return '$12.45';
    if (daysSinceUsed < 7) return '$8.20';
    if (daysSinceUsed < 30) return '$3.15';
    return '$0.50';
  }

  // === USER MANAGEMENT ===

  // Submit access request (public endpoint, no auth required)
  async requestAccess(requestData) {
    console.log('🔍 Submitting access request for:', requestData.email);

    try {
      const response = await fetch(`${this.baseURL}/admin/users/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      const data = await this.handleResponse(response);
      console.log('✅ Access request submitted successfully');
      return data;
    } catch (error) {
      console.error('❌ Failed to submit access request:', error);
      throw error;
    }
  }

  // Get pending requests (admin only)
  async getPendingRequests() {
    console.log('🔍 Fetching pending requests...');

    try {
      const response = await fetch(`${this.baseURL}/admin/users/pending`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log(`✅ Found ${data.total} pending requests`);

      // Calculate days since requested for each pending request
      const requestsWithAge = data.pending_requests.map(request => ({
        ...request,
        requested_days_ago: Math.floor(
          (Date.now() - (request.requested_at * 1000)) / (1000 * 60 * 60 * 24)
        )
      }));

      return {
        ...data,
        pending_requests: requestsWithAge
      };
    } catch (error) {
      console.error('❌ Failed to fetch pending requests:', error);
      throw error;
    }
  }

  // Approve user (admin only)
  async approveUser(userId, options = {}) {
    console.log('✅ Approving user:', userId);

    try {
      const response = await fetch(`${this.baseURL}/admin/users/approve`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ user_id: userId, ...options })
      });

      const data = await this.handleResponse(response);
      console.log('✅ User approved successfully');
      return data;
    } catch (error) {
      console.error('❌ Failed to approve user:', error);
      throw error;
    }
  }

  // Reject user (admin only)
  async rejectUser(userId, reason = null) {
    console.log('❌ Rejecting user:', userId);

    try {
      const response = await fetch(`${this.baseURL}/admin/users/reject`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ user_id: userId, reason })
      });

      const data = await this.handleResponse(response);
      console.log('✅ User rejected successfully');
      return data;
    } catch (error) {
      console.error('❌ Failed to reject user:', error);
      throw error;
    }
  }

  // Complete profile for JIT-provisioned users
  async completeProfile(profileData) {
    console.log('📝 Completing profile for JIT user');

    try {
      const response = await fetch(`${this.baseURL}/admin/users/complete-profile`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(profileData)
      });

      const data = await this.handleResponse(response);
      console.log('✅ Profile completed, awaiting approval');
      return data;
    } catch (error) {
      console.error('❌ Failed to complete profile:', error);
      throw error;
    }
  }

  // Get current EULA version (called at every login for gate check)
  async getEulaVersion() {
    console.log('🔍 Fetching current EULA version...');

    try {
      const response = await fetch(`${this.baseURL}/admin/eula/version`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log('✅ EULA version:', data.version);
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch EULA version:', error);
      throw error;
    }
  }

  // Get EULA content (called only when the gate fires)
  async getEulaContent() {
    console.log('📄 Fetching EULA content...');

    try {
      const response = await fetch(`${this.baseURL}/admin/eula/content`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log('✅ EULA content loaded, version:', data.version);
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch EULA content:', error);
      throw error;
    }
  }

  // Accept EULA for existing users (pass version from getEulaContent response)
  async acceptEula(version) {
    console.log('📝 Accepting EULA version:', version);

    try {
      const response = await fetch(`${this.baseURL}/admin/users/accept-eula`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ version })
      });

      const data = await this.handleResponse(response);
      console.log('✅ EULA acceptance saved');
      return data;
    } catch (error) {
      console.error('❌ Failed to accept EULA:', error);
      throw error;
    }
  }

  // List users (admin only)
  async getUsers(filters = {}) {
    console.log('🔍 Fetching users with filters:', filters);

    try {
      const query = new URLSearchParams(filters).toString();
      const response = await fetch(`${this.baseURL}/admin/users?${query}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await this.handleResponse(response);
      console.log(`✅ Found ${data.total} users`);
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch users:', error);
      throw error;
    }
  }
}

// Export singleton instance
const adminService = new AdminService();
export default adminService;